import { Box } from '@mentimeter/ragnar-ui/box';
import { scaleBand, scaleLinear, scaleOrdinal } from 'd3-scale';
import type { Visualization } from '../types';
import type {
  RenderChoiceLabel,
  RenderDimensionLabels,
} from '../utils/types/render-options-by-slide-type/types';
import { ScalesDimensionLabels } from './ScalesDimensionLabels';
import { ScalesOptionLabels } from './ScalesOptionLabels';
import {
  ScalesVisualization,
  type ScalesVisualizationProps,
} from './ScalesVisualization';
import { MARGIN_BOTTOM, MARGIN_HORIZONTAL, MAX_ITEM_HEIGHT } from './constants';
import type { ScalesData, ScalesOptions, ScalesScore } from './types';

interface ScaleValueClickProps {
  choiceId: ScalesOptions['id'];
  value: number;
}

interface ScalesProps
  extends Omit<Visualization<ScalesData, ScalesOptions>, 'valueAccessor'> {
  rangeMin: number;
  rangeMax: number;
  rangeMinLabel: string;
  rangeMaxLabel: string;
  valueAccessor?: (d: ScalesData) => ScalesScore;
  skipValueAccessor?: (d: ScalesData) => number;
  isShowAverageEnabled?: boolean;
  overallAverageColor: string;
  onScaleValueClick: ((props: ScaleValueClickProps) => void) | undefined;
  activeScale: {
    choiceId: ScalesOptions['id'] | null;
    value: number | null;
  };
  backgroundColor: string;
  renderChoiceVisualization?:
    | ((props: ScalesVisualizationProps) => React.ReactNode)
    | undefined;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
  renderDimensionLabels?: RenderDimensionLabels | undefined;
}

// get max score of array of scores
const getMaxScore = (d: ScalesScore) => Math.max(...Object.values(d));

export const Scales = ({
  width,
  height,
  staticOptions,
  rangeMin,
  rangeMax,
  rangeMinLabel,
  rangeMaxLabel,
  data,
  colors,
  textColor,
  backgroundColor,
  overallAverageColor,
  isShowAverageEnabled = false,
  disableAnimations = false,
  keyAccessor = (d: ScalesData | ScalesOptions) => d.id.toString(),
  labelAccessor = (d: ScalesOptions) => d.label,
  valueAccessor = (d: ScalesData) => d.score,
  skipValueAccessor = (d: ScalesData) => d.skips,
  onScaleValueClick,
  activeScale,
  fontFamily,
  renderChoiceLabel,
  renderChoiceVisualization = ScalesVisualization,
  renderDimensionLabels = ScalesDimensionLabels,
}: ScalesProps) => {
  const rootHeight = Math.max(height, 0);
  const rootWidth = Math.max(width, 0);
  const dimensionsLabelsContainerHeight = rootHeight * 0.2;

  const svgWidth = Math.max(rootWidth, 0);
  const svgHeight = Math.max(rootHeight - dimensionsLabelsContainerHeight, 0);
  const boundedSvgWidth = Math.max(rootWidth - MARGIN_HORIZONTAL * 2, 0);

  const maxBoundedSvgHeight = MAX_ITEM_HEIGHT * staticOptions.length;
  const boundedSvgHeight = Math.min(
    Math.max(svgHeight - MARGIN_BOTTOM * 2, 0),
    maxBoundedSvgHeight,
  );

  const discreteValues = staticOptions.map(keyAccessor);
  const fillColors = scaleOrdinal(colors).domain(discreteValues);

  const scalablePaddingValue = Math.max(
    0.2 - 0.025 * (staticOptions.length - 3),
    0,
  );
  const paddingInner = staticOptions.length > 1 ? scalablePaddingValue : 0; // dont apply padding if there is only one item
  const yScale = scaleBand()
    .domain(discreteValues)
    .range([0, boundedSvgHeight])
    .paddingInner(paddingInner);

  const itemHeight = Math.min(yScale.bandwidth(), MAX_ITEM_HEIGHT);
  const areaMarginTop = itemHeight * 0.15;
  const areaMarginBottom = itemHeight * 0.05;
  const boundsTopMargin = (svgHeight - boundedSvgHeight) / 2;

  const maxScore = Math.max(
    ...data.map((d) => getMaxScore(valueAccessor(d))),
    1, // we need a max value > 0 to be able to animate to 0
  );
  const itemYScale = scaleLinear()
    .domain([0, maxScore])
    .range([itemHeight - areaMarginBottom, areaMarginTop]);

  const xScale = scaleLinear()
    .domain([rangeMin, rangeMax])
    .range([0, boundedSvgWidth]);

  const visualization = renderChoiceVisualization({
    rangeMin,
    valueAccessor,
    skipValueAccessor,
    overallAverageColor,
    onScaleValueClick,
    activeScale,
    backgroundColor,
    svgWidth,
    svgHeight,
    xScale,
    yScale,
    boundsTopMargin,
    staticOptions,
    keyAccessor,
    data,
    fillColors,
    itemYScale,
    itemHeight,
    boundedSvgWidth,
    isShowAverageEnabled,
    textColor,
    fontFamily,
    disableAnimations,
  });

  const dimensionLabels = renderDimensionLabels?.({
    rangeMinLabel,
    rangeMaxLabel,
    height: dimensionsLabelsContainerHeight,
  });

  if (
    svgHeight <= 0 ||
    svgWidth <= 0 ||
    boundedSvgHeight <= 0 ||
    boundedSvgWidth <= 0
  ) {
    return null;
  }

  return (
    <Box
      width={rootWidth}
      height={rootHeight}
      justifyContent="center"
      alignItems="center"
    >
      {visualization}
      <ScalesOptionLabels
        staticOptions={staticOptions}
        keyAccessor={keyAccessor}
        labelAccessor={labelAccessor}
        width={boundedSvgWidth}
        yScale={yScale}
        height={boundedSvgHeight}
        marginTop={boundsTopMargin}
        itemHeight={itemHeight}
        color={textColor}
        fontFamily={fontFamily}
        renderChoiceLabel={renderChoiceLabel}
      />
      {dimensionLabels}
    </Box>
  );
};
